import { Button, Link } from '@mui/material';
import { GridColDef, ValueOptions } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentEditCellComponent } from '~/components/SolaForceDataGrid';
import {
  UseDataGridCRUDValidationOutput,
  UseDataGridRowFileChangesState,
  crudCellClassName,
  useDataGridCRUDColumns,
} from '~/components/SolaForceDataGrid/hooks';
import { conditionalGridColDefs } from '~/components/SolaForceDataGrid/utils';
import { Enum } from '~/data/enums';
import { DocumentsEntry } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/data';
import { getEmployeeAttachmentUrl } from '~/utils/helper';

export type UseDocumentsTableColumnsProps = {
  selectedEmployeeId: number;
  documentTypes: Enum[];
  documentApprovalStatuses: Enum[];
  documentApproval: {
    onDocumentApproval: (row: DocumentsEntry) => void;
    enabled: boolean;
  };
  canEdit: boolean;
} & Pick<UseDataGridCRUDValidationOutput<DocumentsEntry>, 'columnsValidation'> &
  Pick<UseDataGridRowFileChangesState, 'updateFileChanges' | 'fileChanges'>;

type UseFormsTableColumnsOutput = {
  columns: GridColDef<DocumentsEntry>[];
};

export const useDocumentsTableColumns = (
  props: UseDocumentsTableColumnsProps,
): UseFormsTableColumnsOutput => {
  const {
    selectedEmployeeId,
    documentTypes,
    documentApprovalStatuses,
    columnsValidation,
    updateFileChanges,
    fileChanges,
    documentApproval: { onDocumentApproval, enabled: documentApprovalEnabled },
    canEdit,
  } = props;

  const { t } = useTranslation();

  const columns: GridColDef<DocumentsEntry>[] = useMemo(() => {
    const dateColumnSizeProps = {
      flex: 0.3,
      minWidth: 130,
    };

    return [
      {
        field: 'type',
        headerName: t('laDocumentType'),
        type: 'singleSelect',
        editable: true,
        flex: 0.5,
        minWidth: 140,
        valueOptions: documentTypes.map(
          (e): ValueOptions => ({
            value: e.code,
            label: e.name,
          }),
        ),
        valueFormatter: ({ value }) => {
          return (
            documentTypes.find((e) => e.code === value)?.name ?? value ?? ''
          );
        },
      },
      {
        field: 'created',
        headerName: t('laCreatedTime'),
        type: 'date',
        ...dateColumnSizeProps,
      },
      {
        field: 'modified',
        headerName: t('laModifiedTime'),
        type: 'date',
        ...dateColumnSizeProps,
      },
      {
        field: 'name',
        headerName: t('laDocumentName'),
        editable: true,
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ value, row }) => {
          return (
            <Link
              href={getEmployeeAttachmentUrl(selectedEmployeeId, row.id)}
              target={'_blank'}
            >
              {value}
            </Link>
          );
        },
        renderEditCell: (params) => {
          return (
            <AttachmentEditCellComponent
              params={params}
              required={true}
              onCellFileChange={({ value, params, apiRef }) => {
                updateFileChanges({ id: params.id, file: value });
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: value?.name ?? '',
                });
              }}
              getAttachmentUrl={({ params }) => {
                const { row } = params;
                if (
                  row.fileSize >= 0 &&
                  !fileChanges.some((fc) => fc.id === row.id)
                ) {
                  return getEmployeeAttachmentUrl(selectedEmployeeId, row.id);
                }
                return undefined;
              }}
            />
          );
        },
      },
      {
        field: 'description',
        headerName: t('laDescription'),
        editable: true,
        flex: 1.5,
        minWidth: 200,
      },
      {
        field: 'expires',
        headerName: t('laExpires'),
        type: 'date',
        editable: true,
        ...dateColumnSizeProps,
      },
      {
        field: 'approval',
        headerName: t('laApproval'),
        valueFormatter: ({ value }) => {
          return (
            documentApprovalStatuses.find((e) => e.code === value)?.name ??
            value ??
            ''
          );
        },
      },
      ...conditionalGridColDefs<DocumentsEntry>(documentApprovalEnabled, {
        field: 'approve',
        headerName: t('laApprove'),
        editable: true,
        cellClassName: crudCellClassName.noEditing,
        renderCell: ({ row, cellMode }) => {
          const { type, ownerType } = row;
          if (
            type !== 'FORM_ATTACHMENT' &&
            ownerType === 'EMPLOYEE' &&
            cellMode === 'view'
          ) {
            return (
              <Button onClick={() => onDocumentApproval(row)}>
                {t('laApprove')}
              </Button>
            );
          }

          return <></>;
        },
        renderEditCell: () => {
          // Hide button when row is on edit mode
          return <></>;
        },
      }),
      ...conditionalGridColDefs<DocumentsEntry>(canEdit, {
        field: 'actions',
        type: 'actions',
        headerName: t('Actions'),
        width: 90,
        cellClassName: 'actions',
      }),
    ];
  }, [
    t,
    documentTypes,
    documentApprovalStatuses,
    selectedEmployeeId,
    updateFileChanges,
    fileChanges,
    documentApprovalEnabled,
    onDocumentApproval,
    canEdit,
  ]);

  return useDataGridCRUDColumns({
    columns,
    columnsValidation,
  });
};
